import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CellSub } from "@/components/portal/crm/kit";

/**
 * Picking desks without typing or searching.
 *
 * Owner: *"what if we want to send the applicant to every lender… rather than typing in and
 * searching we need a clear nice way to select lenders our applicants go to"*. So: one tap for the
 * whole panel, one for the three best-fitting desks, one to clear — and his explicit choice that
 * "every desk" means **genuinely every matched desk, paused and over-cap included**, with the
 * warning line saying so rather than quietly dropping them.
 */

const flagged = (m) => !!(m.capacity?.paused || m.capacity?.at_capacity);

const Pick = ({ children, onClick, disabled, testId, strong }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
          className={`btn btn-${strong ? "primary" : "ghost"} btn-sm`}>
    {children}
  </button>
);

export function RoutePicks({ options = [], selected = [], onChange, testPrefix = "route-picks" }) {
  if (!options.length) return null;

  const ids = options.map((m) => m.id);
  const byFit = [...options].sort((a, b) => (b.fit?.score || 0) - (a.fit?.score || 0));
  const all = selected.length === ids.length;
  const flaggedPicked = options.filter((m) => selected.includes(m.id) && flagged(m));

  return (
    <div data-testid={testPrefix}
         style={{ display: "flex", flexDirection: "column", gap: 9, padding: "13px 15px", marginBottom: 14,
           border: "1px solid var(--line-2)", borderRadius: 12, background: "var(--soft, rgba(11,18,32,.025))" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
        <Pick strong={!all} onClick={() => onChange(all ? [] : ids)} testId={`${testPrefix}-all`}>
          {all ? "Unpick them all" : `Every desk · ${ids.length}`}
        </Pick>
        {ids.length > 3 && (
          <Pick onClick={() => onChange(byFit.slice(0, 3).map((m) => m.id))} testId={`${testPrefix}-top3`}>
            Best 3 by fit
          </Pick>
        )}
        {ids.length > 5 && (
          <Pick onClick={() => onChange(byFit.slice(0, 5).map((m) => m.id))} testId={`${testPrefix}-top5`}>
            Best 5
          </Pick>
        )}
        <Pick onClick={() => onChange([])} disabled={!selected.length} testId={`${testPrefix}-clear`}>
          Clear
        </Pick>
        <span style={{ marginLeft: "auto", font: "700 13px Archivo", color: "var(--ink)" }}
              data-testid={`${testPrefix}-count`}>
          {selected.length} of {ids.length} picked
        </span>
      </div>

      {flaggedPicked.length > 0 && (
        <CellSub style={{ margin: 0 }}>
          <span data-testid={`${testPrefix}-warning`}>
            {flaggedPicked.length} of these {flaggedPicked.length === 1 ? "is" : "are"} paused or over
            their cap — {flaggedPicked.map((m) => m.company_name).filter(Boolean).join(", ")}. They
            still get the case, flagged for a human on their side.
          </span>
        </CellSub>
      )}
    </div>
  );
}

/**
 * The honest state of auto-routing, right where a case is being routed by hand — so it is never a
 * mystery whether the pingtree is also sending this on its own.
 */
export function AutoRoutingNote({ testId = "auto-routing-note" }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    api.get("/hub/rules")
      .then((r) => setState({ on: !!r.data?.auto_routing_enabled, rules: r.data?.total || 0 }))
      .catch(() => setState(null));
  }, []);

  if (!state) return null;
  return (
    <CellSub style={{ margin: "0 0 12px" }}>
      <span data-testid={testId}>
        {state.on
          ? `Auto-routing is on · ${state.rules} rule${state.rules === 1 ? "" : "s"} can send a new case to desks by itself.`
          : "Auto-routing is off — every case goes out by hand until you switch it on."}{" "}
        <Link to="/hub/rules" data-testid={`${testId}-link`} className="text-[#0044CC] font-semibold">
          {state.on ? "Review the rules" : "Set it up"}
        </Link>
      </span>
    </CellSub>
  );
}
